import { useState, useEffect, useCallback } from "react";
import LateralMenu from "../../components/LateralMenu/LateralMenu";
import { 
  Box, 
  Paper, 
  Typography, 
  Container,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  TablePagination,
  CircularProgress,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@mui/material";
import { CodificationItem } from "../../components/CodificationItem/CodificationItem";
import { pesquisaApi } from "../../services/apiPesquisa.servise";
import { resetState } from "../../store";
import { useLocation } from "react-router-dom";
import { Loading } from "../../components/Loading";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from "@mui/icons-material";
import { authService } from "../../services/auth.service";

const Home = () => {
  const location = useLocation();
  const anotherUserId = location.state && location.state.anotherUserId ? location.state.anotherUserId : null;
  const user = authService.getLoggedUser();
  const isAdm = user?.isAdm || false;
  
  const [headers, setHeaders] = useState([]);
  const [openBackdrop, setOpenBackDrop] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  const [searchText, setSearchText] = useState('');
  const [searchTextSubmitted, setSearchTextSubmitted] = useState('');
  const [filters, setFilters] = useState({
    hasImgs: 'all',
    ordering: '-id_header'
  });
  
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showAll, setShowAll] = useState(false);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const loadHeaders = useCallback(async (pageNum = 0) => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      const params = {
        page: (pageNum + 1).toString(),
        page_size: rowsPerPage.toString(),
        ordering: filters.ordering
      };
      
      if (searchTextSubmitted) {
        params.search = searchTextSubmitted;
      }
      
      if (filters.hasImgs !== 'all') {
        params.hasImgs = filters.hasImgs;
      }
      
      console.log('📋 Parâmetros da requisição:', params);
      
      // Lógica de seleção do endpoint
      if (showAll && isAdm) {
        // Admin vê todas as codificações
        response = await pesquisaApi.getAllHeaders(
          pageNum + 1,
          rowsPerPage,
          searchTextSubmitted,
          params
        );
      } else if (anotherUserId) {
        // Visualização de outro usuário
        response = await pesquisaApi.getHeadersAnotherUser(
          anotherUserId,
          pageNum + 1,
          rowsPerPage,
          searchTextSubmitted,
          params
        );
      } else {
        // Visualização padrão (apenas do usuário logado)
        response = await pesquisaApi.getHeaders(
          pageNum + 1,
          rowsPerPage,
          searchTextSubmitted,
          params
        );
      }
      
      if (response && response.data) {
        if (response.data.results) {
          setHeaders(response.data.results);
          setTotalCount(response.data.count || 0);
        } else {
          setHeaders(response.data);
          setTotalCount(response.data.length);
        }
      } else {
        setHeaders([]);
        setTotalCount(0);
        console.log('⚠️ Nenhum dado retornado pela API');
      }
      
    } catch (err) {
      console.error('❌ Erro ao carregar codificações:', err);
      setError(err.message);
      setSnackbar({
        open: true,
        message: `Erro ao carregar codificações: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setOpenBackDrop(false);
    }
  }, [page, rowsPerPage, filters, searchTextSubmitted, anotherUserId, showAll, isAdm]);

  useEffect(() => {
    resetState();
    setOpenBackDrop(true);
    
    const timeoutId = setTimeout(() => {
      loadHeaders(page);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, filters.ordering, filters.hasImgs, searchTextSubmitted, showAll, loadHeaders]);

  const handleSearch = () => {
    setSearchTextSubmitted(searchText);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSearchTextSubmitted('');
    setFilters({
      hasImgs: 'all',
      ordering: '-id_header'
    });
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const handleRefresh = () => {
    loadHeaders(page);
  };

  const handleCodificationUpdate = () => {
    loadHeaders(page);
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
    setPage(0);
  };

  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        {/* Campo de busca */}
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            placeholder="Buscar por nome da via ou trecho..."
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              sx={{ flex: 1 }}
            >
              Buscar
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              startIcon={<FilterListIcon />}
              sx={{ flex: 1 }}
            >
              Filtros
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Filtros avançados */}
      {showAdvancedFilters && (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            Filtros Avançados
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status de Imagens</InputLabel>
                <Select
                  value={filters.hasImgs}
                  label="Status de Imagens"
                  onChange={(e) => handleFilterChange('hasImgs', e.target.value)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="true">Com imagens</MenuItem>
                  <MenuItem value="false">Sem imagens</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={filters.ordering}
                  label="Ordenar por"
                  onChange={(e) => handleFilterChange('ordering', e.target.value)}
                >
                  <MenuItem value="-id_header">Mais recentes</MenuItem>
                  <MenuItem value="id_header">Mais antigos</MenuItem>
                  <MenuItem value="road_name">Nome da via (A-Z)</MenuItem>
                  <MenuItem value="-road_name">Nome da via (Z-A)</MenuItem>
                  <MenuItem value="section_name">Trecho (A-Z)</MenuItem>
                  <MenuItem value="-section_name">Trecho (Z-A)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  fullWidth
                >
                  Limpar Filtros
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleRefresh}
                  startIcon={<RefreshIcon />}
                  fullWidth
                >
                  Atualizar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );

  const renderStats = () => {
    const totalWithImages = headers.filter(h => h.hasImgs).length;
    const totalWithoutImages = headers.length - totalWithImages;
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: "1px solid #d4d4d4" }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Codificações
              </Typography>
              <Typography variant="h4">
                {totalCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: "1px solid #d4d4d4" }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Com Imagens
              </Typography>
              <Typography variant="h4" color="success.main">
                {totalWithImages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: "1px solid #d4d4d4" }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Sem Imagens
              </Typography>
              <Typography variant="h4" color="text.secondary">
                {totalWithoutImages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: "1px solid #d4d4d4" }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Página Atual
              </Typography>
              <Typography variant="h4">
                {page + 1}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderContent = () => {
    if (loading && headers.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          {/* <CircularProgress /> */}
        </Box>
      );
    }
    
    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Erro ao carregar codificações
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      );
    }
    
    if (headers.length === 0) {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1">
            Nenhuma codificação encontrada
          </Typography>
          <Typography variant="body2">
            {searchTextSubmitted 
              ? `Nenhum resultado para "${searchTextSubmitted}"` 
              : 'Comece criando sua primeira codificação'}
          </Typography>
        </Alert>
      );
    }
    
    return (
      <>
        {headers.map((item) => (
          <CodificationItem
            key={item.id_header}
            lastDateAlteretion={item.coding_date}
            codificationTitle={item.road_name}
            lastSectionName={item.section_name}
            idHeader={item.id_header}
            item={item}
            hasImgs={item.hasImgs}
            onUpdate={handleCodificationUpdate}
          />
        ))}
      </>
    );
  };

  return (
    <>
      {openBackdrop && <Loading />}
      <LateralMenu isHome={true} />
      
      <Box sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#e7eaef",
      }}>
        <Container maxWidth="lg" sx={{ p: { xs: 1, sm: 2, md: 3 }, mt: 4, mb: 4, mt: { xs: 6, sm: 8 } }}>
          {/* Cabeçalho */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }} align="center">
              <Typography variant="h4" gutterBottom color="primary">
                {anotherUserId ? 'Codificações do Usuário' : 'Minhas Codificações'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {anotherUserId 
                  ? 'Visualize e gerencie as codificações deste usuário' 
                  : 'Administre e consulte suas codificações'}
              </Typography>
            </Box>

            {/* Botão para AdmEncoder visualizar todas as codificações */}
            {isAdm && !anotherUserId && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant={showAll ? "contained" : "outlined"}
                  color="primary"
                  // sx={{p: 1}}
                  onClick={toggleShowAll}
                  startIcon={showAll ? <VisibilityOffIcon /> : <VisibilityIcon />}
                >
                  {showAll ? 'Ver Minhas Codificações' : 'Ver Todas as Codificações'}
                </Button>
              </Box>
            )}
            
            {/* Estatísticas */}
            {/* {!loading && headers.length > 0 && renderStats()} */}
            
            {/* Filtros */}
            {renderFilters()}
            
            {/* Lista de codificações */}
            <Paper elevation={0} sx={{ mb: 3 }}>
              {renderContent()}
            </Paper>
            
            {/* Paginação */}
            {!loading && headers.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Codificações por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
              />
            )}
          </Paper>
          
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default Home;